// What a caller hears at one point: an audio file, text-to-speech, or nothing.
// The audio picker (asset store) is injected by the host once the audio store
// exists; until then an audio prompt shows which Teams recording it refers to.

import type { ReactNode } from 'react';
import { Volume2 } from 'lucide-react';
import type { Prompt } from '../../api/client';
import { inputCls, labelCls } from './common';

export interface PromptEditorProps {
  label: string;
  value?: Prompt;
  onChange: (v: Prompt | undefined) => void;
  /** Renders the audio-file chooser for an audio prompt (asset store). */
  audioPicker?: (value: Prompt, onChange: (v: Prompt) => void) => ReactNode;
  hint?: string;
}

export function PromptEditor({ label, value, onChange, audioPicker, hint }: PromptEditorProps) {
  const mode = value?.mode || 'none';
  const set = (patch: Partial<Prompt>) => {
    const base: Prompt = value ? { ...value } : { mode };
    const next: Prompt = { ...base, ...patch };
    if (next.mode === 'none' && !next.text && !next.audioId && !next.teamsAudioFileId) { onChange(undefined); return; }
    onChange(next);
  };
  return (
    <div className="border border-gray-100 rounded p-2 bg-gray-50/60">
      <div className="flex items-center gap-2 mb-1.5">
        <label className={`${labelCls} mb-0 flex items-center gap-1`}><Volume2 className="w-3.5 h-3.5 text-gray-400" />{label}</label>
        <select value={mode} onChange={e => set({ mode: e.target.value as Prompt['mode'] })}
          className="ml-auto px-2 py-1 text-xs border border-gray-200 rounded bg-white">
          <option value="none">None</option>
          <option value="tts">Text to speech</option>
          <option value="audio">Audio file</option>
        </select>
      </div>
      {mode === 'tts' && (
        <textarea value={value?.text || ''} onChange={e => set({ text: e.target.value || undefined })} rows={2}
          placeholder="Welcome to GoodLife Foods. For Dutch, press 1…" className={inputCls} />
      )}
      {mode === 'audio' && (
        <div className="space-y-1.5">
          {audioPicker ? audioPicker(value || { mode: 'audio' }, v => onChange(v)) : (
            <div className="text-xs text-gray-500">
              {value?.teamsFileName || value?.teamsAudioFileId
                ? <>Teams recording: <span className="font-medium text-gray-700">{value.teamsFileName || value.teamsAudioFileId}</span></>
                : 'Audio file'}
            </div>
          )}
          <input type="text" value={value?.text || ''} onChange={e => set({ text: e.target.value || undefined })}
            placeholder="Transcript / what is said (optional)" className={inputCls} />
        </div>
      )}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
